import { defineStore } from 'pinia'
import {
  getTasksList,
  taskSave,
  inspirationUpdate,
  getTaskDetail,
  setTaskRead
} from '../service/task'
import { generateContent } from '../service/llm'
import { convertEventStreamToIterableReadableDataStream } from '@langchain/core/utils/event_source_parse'
import { getRandomWord, getHotTopic, getHotTopicByName } from '../service/task'
import { useLayoutStore } from './layoutStore'
import { useLLMStore } from './llm'
import { ElMessage } from 'element-plus'
import dayjs from 'dayjs'
import { modeEnum } from '../utils/enums'

const keyWordFetch = {
  随机模式: getRandomWord,
  热点模式: getHotTopic
}

export const useTaskStore = defineStore('task', {
  state: () => ({
    rawTasks: {},
    disabledBasicItemMap: {},
    keyWordMap: {},
    popperInstance: null,
    allTotal: 0,
    searchKey: '',
    currentTaskDetail: {},
    currentEditTask: {},
    defaultGenerateKeys: ['insp_1', 'insp_2', 'insp_3'],
    dialogLoadingRef: false,
    results: {},
    defaultTask: {
      name: '新任务',
      status: '草稿',
      content: {
        product_name: '',
        benefits: '',
        target_audience: '',
        script_scene: '',
        script_style: '口播',
        script_mode: '常规模式',
        keywords: [],
        structure_begin: [],
        structure_end: [],
        structure_middle: [],
        structure_transition: [],
        structure_random: false,
        number_of_character: '',
        character_profile: ''
      },
      advanced: {
        frequency_penalty: 0,
        temperature: 0.7,
        top_p: 1
      }
    }
  }),
  getters: {
    tasksBySort: (state) => {
      return Object.values(state.rawTasks)
        .sort((a, b) => b.id - a.id)
        .filter((task) => {
          return task.name?.includes(state.searchKey) ?? true
        })
    },

    variables: (state) => {
      return {
        ...state.currentTaskDetail.content
      }
    },
    currentResult: (state) => {
      return useLLMStore().results[state.currentTaskDetail.id] ?? {}
    },

    categorizedTasks: (getters) => {
      const categorized = {}
      // 使用 tasks 作为数据源
      getters.tasksBySort.forEach((task) => {
        const createAt = dayjs(task.create_time).startOf('day')
        const diffDays = dayjs().diff(createAt, 'day')
        if (diffDays === 0) {
          if (!categorized['今天']) {
            categorized['今天'] = []
          }
          categorized['今天'].push(task)
        } else if (diffDays === 1) {
          if (!categorized['昨天']) {
            categorized['昨天'] = []
          }
          categorized['昨天'].push(task)
        } else if (diffDays > 1 && diffDays <= 7) {
          if (!categorized['前7天']) {
            categorized['前7天'] = []
          }
          categorized['前7天'].push(task)
        } else {
          if (!categorized['更早']) {
            categorized['更早'] = []
          }
          categorized['更早'].push(task)
        }
      })

      return categorized
    },

    currentTaskId: (state) => {
      return state.currentTaskDetail.id
    },

    isIndustryIn: (state) => (industryIds) => {
      return industryIds.includes(state.currentTaskDetail.industry_id)
    },

    fieldsOfIndustry: () => (industryId) => {
      if (industryId == 1) {
        return [
          'product_name',
          'benefits',
          'benefits_unfixed',
          'target_audience',
          'script_scene',
          'script_style',
          'script_mode'
        ]
      } else if (industryId == 2) {
        return [
          'product_name',
          'benefits',
          'structure_begin',
          'structure_end',
          'structure_middle',
          'structure_transition',
          'number_of_character',
          'character_profile',
          'script_scene',
        ]
      } else if (industryId == 3) {
        return ['product_name', 'benefits', 'character_profile']
      }
    },

    fieldShouldDisplay: (state) => (field, industryId=null) => {
      if (!industryId) {
        industryId = state.currentTaskDetail.industry_id
      }
      return state.fieldsOfIndustry(industryId).includes(field)
    }
  },
  actions: {
    fetchTaskList() {
      return getTasksList()
    },
    updateTaskList(tasks = []) {
      this.rawTasks = {
        ...this.rawTasks,
        ...tasks.reduce((acc, task) => {
          if (!task) return acc
          acc[task.id] = task
          return acc
        }, {})
      }

      tasks.forEach((task) => {
        this.rawTasks[task.id].status = task.status
      })
    },

    updateTaskDetail(params = {}) {
      this.currentTaskDetail = { ...this.currentTaskDetail, ...params }
      // 更新 generated_content 数组
      if (params.generated_content) {
        this.currentTaskDetail.generated_content = [
          ...(this.currentTaskDetail.generated_content || []),
          ...params.generated_content.map((item) => ({
            ...item,
            parent_snapshot: this.currentTaskDetail.content
          }))
        ]
      }
    },

    // 新增一个 action 用于追加 generated_content 并更新整个 currentTaskDetail
    appendGeneratedContentAndUpdateTask(newContent, taskDetail) {
      if (!this.currentTaskDetail.generated_content) {
        this.currentTaskDetail.generated_content = []
      }
      this.currentTaskDetail.generated_content.push(
        ...newContent.map((item) => ({
          ...item,
          parent_snapshot: this.currentTaskDetail.content
        }))
      )
      // 更新整个 currentTaskDetail 对象
      this.currentTaskDetail = {
        ...this.currentTaskDetail,
        ...taskDetail,
        generated_content: this.currentTaskDetail.generated_content
      }
    },

    setTaskDetail(task) {
      this.currentTaskDetail = task
    },
    deleteTask(id) {
      delete this.rawTasks[id]
    },

    setCurrentTaskDetail(task) {
      this.currentTaskDetail = task
    },
    setCurrentEditTask(task) {
      this.currentEditTask = task
    },

    async processStreamData(taskId, stream) {
      // 这里还需要重构
      const results = useLLMStore().results
      // 处理流数据 有待优化
      for await (const chunk of stream) {
        const { data, code } = JSON.parse(chunk)
        if (!data) return // 如果没有数据则直接返回

        if (!results[taskId]) {
          results[taskId] = {
            tasks_generated: false
          }
        } else {
          results[taskId].tasks_generated = false
        }
        if (!data.key) {
          results[taskId].tasks_generated = true
          return
        }
        let taskResult = results[taskId][data.key]
        if (!taskResult) {
          taskResult = results[taskId][data.key] = {
            content: '',
            create_time: '',
            parent_key: data.key,
            number: data.key.split('_')[1],
            taskId: taskId,
            parent_id: taskId,
            generateStatus: '生成中',
            event: modeEnum[this.currentTaskDetail.content.script_mode]
          } //只初始化不会变的结果
        } else {
          taskResult.generateStatus = '生成中'
        }
        taskResult.content += data.content
        if (code === 101) {
          taskResult.generateStatus = '生成完毕'
          taskResult.create_time = data.create_time
        }
        if (code === 200) {
          taskResult.generateStatus = '生成失败'
        }
      }
    },

    resetFields() {
      const currentTaskId = this.currentTaskDetail.id
      const isDraft = this.rawTasks[currentTaskId].status === '草稿'

      // 重置表单内容
      if (isDraft) {
        this.currentTaskDetail.content = {
          product_name: '',
          benefits: '',
          benefits_unfixed: '',
          target_audience: '',
          script_scene: '',
          script_style: '口播',
          script_mode: '常规模式',
          keywords: [],
          structure_begin: [],
          structure_end: [],
          structure_middle: [],
          structure_transition: [],
          structure_random: false,
          number_of_character: '',
          character_profile: ''
        }
      } else {
        const content = this.currentTaskDetail.content
        content.benefits = ''
        content.target_audience = ''
        content.script_scene = ''
        content.structure_begin = []
        content.structure_end = []
        content.structure_middle = []
        content.structure_transition = []
        content.structure_random = false
        content.number_of_character = ''
        content.character_profile = ''
        this.script_style = '口播'
      }
    },

    clearPopper() {
      if (this.popperInstance) {
        this.popperInstance.destroy()
        this.popperInstance = null
      }
    },

    async prepareGenerateTask(params) {
      const currentTaskId = params.task_id
      const body = {
        ...params,
        advanced: this.currentTaskDetail.advanced
      }
      this.setRawTaskStatus({
        id: currentTaskId,
        status: '生成中'
      })
      return { currentTaskId, body }
    },

    async handleGeneratingContent(currentTaskId, body) {
      try {
        const res = await generateContent({
          taskId: currentTaskId,
          payload: {
            body: JSON.stringify(body)
          }
        })
        const stream = convertEventStreamToIterableReadableDataStream(res.body)
        await this.processStreamData(currentTaskId, stream)
      } catch (error) {
        console.error(error)
        ElMessage.error('生成失败')
      }
    },

    async generateTask(params) {
      const { currentTaskId, body } = await this.prepareGenerateTask(params)
      await this.handleGeneratingContent(currentTaskId, body)
    },

    handleTaskSave(task_id, key, params) {
      return taskSave(task_id, key, params)
    },
    HandleInspirationUpdate(task_id, key, params) {
      return inspirationUpdate(task_id, key, params)
    },

    mergeWithDefault(defaultObj, networkObj) {
      if (!networkObj) return defaultObj
      const newData = Object.keys({ ...defaultObj, ...networkObj }).reduce((result, key) => {
        // 如果 networkObj 中存在该 key，则使用 networkObj 的值
        result[key] = networkObj[key] ? networkObj[key] : defaultObj[key]
        // 递归合并子对象或数组
        if (typeof result[key] === 'object' && result[key] !== null) {
          if (Array.isArray(result[key])) {
            // 如果是数组则遍历数组进行合并
            result[key] = networkObj[key] || []
          } else {
            // 如果是对象，则递归调用 mergeData
            result[key] = this.mergeWithDefault(defaultObj[key], networkObj[key])
          }
        }
        return result
      }, {})

      return newData
    },

    async initializeKeywordsMap(mergedResult, scriptMode) {
      const currentTaskId = this.currentTaskDetail.id
      const { keywords } = mergedResult.content
      let keywordDetail

      // 处理随机模式的关键词
      if (scriptMode === '随机模式') {
        keywordDetail = {
          随机模式: [keywords]
        }
      }
      // 处理热点模式的关键词
      if (scriptMode === '热点模式') {
        const { data: hotTopicDetail } = await getHotTopicByName(keywords.toString()) // 获取关键词热点详情
        keywordDetail = {
          热点模式: [hotTopicDetail]
        }
      }
      this.keyWordMap[currentTaskId] = keywordDetail
    },

    async handleTaskRead(id) {
      const currentTaskId = this.currentTaskDetail.id
      if (id !== currentTaskId) return
      const { code } = await setTaskRead(id)
      if (code === 0) {
        this.setRawTaskStatus({
          id,
          status: '生成完毕-用户已读'
        })
      }
    },

    handleGeneratedContent() {
      // 处理生成完毕的内容
      const { generated_content } = this.currentTaskDetail
      const currentTaskId = this.currentTaskDetail.id
      //出现在generated_content中的key都是已经生成完毕的
      useLLMStore().setResults({
        taskId: currentTaskId,
        results: generated_content.reduce((acc, item) => {
          acc[item.parent_key] = {
            ...item,
            generateStatus: '生成完毕'
          }
          acc.tasks_generated = true // 任务已经生成完毕
          return acc
        }, {})
      })
    },

    startGeneratingTasks(taskId) {
      this.currentTaskDetail.generating.forEach((item) => {
        this.generateTask({
          variables: this.variables,
          keys: [item.key],
          llm_type: item.event,
          task_id: taskId
        })
      })
    },

    processTask(taskId) {
      if (this.currentTaskDetail.generated_content?.length) {
        this.handleGeneratedContent(taskId) // 处理生成完成的内容
      }
      if (this.currentTaskDetail.generating?.length) {
        // 如果有正在生成的内容
        this.startGeneratingTasks(taskId) // 处理正在生成中的内容
      }
    },

    async initializeTaskDetail(taskId) {
      const layoutStore = useLayoutStore()
      // 显示加载指示器
      layoutStore.setDialogLoadingRef(true)

      // 获取任务详情
      const { code, data } = await getTaskDetail(taskId)

      if (code !== 0) {
        ElMessage.error('获取任务详情失败')
        return
      }
      const { status } = data
      this.allTotal = data.generated_content_count
      // 合并任务详情和默认值
      const mergedResult = this.mergeWithDefault(this.defaultTask, data)
      const scriptMode = mergedResult.content.script_mode
      // 更新任务详情
      this.updateTaskDetail({
        ...mergedResult,
        taskId
      })

      // 关闭加载指示器
      layoutStore.setDialogLoadingRef(false)

      // 初始化disabled map 用于禁用表单 如果当前任务不是草稿状态 则禁用表单
      if (!this.disabledBasicItemMap[taskId]) {
        this.disabledBasicItemMap[taskId] = status !== '草稿'
      }

      // 同步列表状态
      this.rawTasks[taskId].status = status

      // if (this.rawTasks[taskId].status === '生成完毕') {
      //   this.handleTaskRead(this.currentTaskDetail.id) // 标记任务已读
      // }

      // 初始化关键词
      this.initializeKeywordsMap(mergedResult, scriptMode)

      // 根据任务状态决是否显示结果页面
      if (status !== '草稿') {
        layoutStore.showResults = true
      }

      // 处理任务
      if (!Object.keys(this.currentResult).length) {
        // 如果没有生成过内容
        this.processTask(taskId)
      }
    },
    describeKeyword(data) {
      if (data.keyword && data.description && data.example) {
        return [
          `关键词"${data.keyword}"指的是${data.description}例如，${data.example?.replace(
            '\n',
            ' '
          )}`
        ]
      } else {
        return [data.keyword]
      }
    },
    async fetchKeywords(value) {
      if (!value) return
      this.currentTaskDetail.content.keywords = []
      const { script_mode: currentMode } = this.currentTaskDetail.content
      const currentTaskId = this.currentTaskDetail.id
      if (!this.keyWordMap[currentTaskId]) {
        this.keyWordMap[currentTaskId] = {}
      }
      if (currentMode === '常规模式' || !currentMode) return
      try {
        const { code, data } = await keyWordFetch[currentMode]()
        if (code === 0) {
          this.keyWordMap[currentTaskId][currentMode] = data.list
        }
      } catch (error) {
        console.error('Error fetching keywords:', error)
      }
    },

    setRawTaskStatus({ id, status }) {
      this.rawTasks[id].status = status
    },

    updateCurrentTaskContent(newContent) {
      this.currentTaskDetail.content = {
        ...this.currentTaskDetail.content,
        ...newContent
      }
    },

    updateBaseInfo(taskId, info) {
      if (this.currentTaskDetail && this.currentTaskDetail.id === taskId) {
        this.currentTaskDetail.content = {
          ...this.currentTaskDetail.content,
          ...info
        }
      }
    }
  }
})
